import { createDecorator } from '../resolvers/definition/decorator';
import {
  ResolverMeta,
  ResolverRuntime,
} from '../resolvers/definition/resolver';
import { PersistenceManager } from './persistenceManager';
import {
  ResolverState,
  toError,
  toLoading,
  toSuccess,
} from '../resolvers/definition/resolverState';
import { PersistConfig } from './types';
import { Metrics } from '../metrics/metrics';

export const createPersistenceDecorator = (
  persistenceManager: NonNullable<PersistenceManager>,
  {
    maxStoredAge = 0,
    maxFallbackAge = maxStoredAge,
    silentFallback = false,
  }: PersistConfig,
  getStoredAt: (instanceKey: string) => number | undefined
) =>
  createDecorator('persistence', (attach) => (params, runtime) => {
    const instanceKey = runtime.env.instanceKey;

    const publishSilently = (
      data: any,
      error: Error,
      meta: ResolverMeta | undefined
    ) => {
      Metrics.counter('persistence.silent-fallback').increment();
      runtime.env.logger?.warn('persistence fallback hid an error', {
        instanceKey,
        error: error.message,
      });
      runtime.next(toSuccess(data)(), meta);
    };

    // With silentFallback, a failed refetch keeps showing the in-memory data
    // as a success while that data is younger than maxFallbackAge.
    const hideRefetchError = (
      state: ResolverState<any, any>,
      meta: ResolverMeta | undefined
    ) => {
      const storedAt = getStoredAt(instanceKey);

      if (
        !silentFallback ||
        !state.error ||
        state.data === undefined ||
        storedAt === undefined ||
        storedAt + maxFallbackAge <= Date.now()
      ) {
        return false;
      }

      publishSilently(state.data, state.error, meta);
      return true;
    };

    if (runtime.getState()?.data !== undefined) {
      return attach(params, {
        ...runtime,
        next: (state, meta) => {
          if (!hideRefetchError(state, meta)) {
            runtime.next(state, meta);
          }
        },
      });
    }

    // SWR
    // If the resolver hasn't yet pushed data and the IDB read resolves with,
    // data that's fresh enough, emit a stale loading state with the IDB's data.
    let resolved = false;

    const idbRead = persistenceManager
      .read(instanceKey)
      .then((entry) => {
        if (!resolved && entry && entry.storedAt + maxStoredAge > Date.now()) {
          runtime.next(toLoading()(toSuccess(JSON.parse(entry.data))()), {
            storedAt: entry.storedAt,
          });
        }

        return entry;
      })
      .catch((err) => {
        runtime.env.logger?.error('persistence hydration failed', {
          instanceKey,
          error: (err as Error).message,
        });
      });

    // Fault tolerance
    // Defer error states until we have a result from IDB.
    // Once we do, publish the deferred state w/ the IDB results if fresh enough.
    let deferredError: { state: any; meta: any } | null = null;

    const wrappedNext: ResolverRuntime<any, any>['next'] = (state, meta) => {
      if (hideRefetchError(state, meta)) {
        deferredError = null;
        return;
      }

      // If a data-ful state is published, the resolver has resolved and we should never
      // publish from IDB.
      if (state.data !== undefined) {
        resolved = true;
      }

      // If the resolver has previously pushed data, or we aren't in an error state,
      // clear the pending error and publish the state.
      if (resolved || !state.error) {
        deferredError = null;
        runtime.next(state, meta);
        return;
      }

      // Always capture the new state, but only fire the IDB read on the first deferral
      const isFirstDeferral = deferredError == null;
      deferredError = { state, meta };

      if (isFirstDeferral) {
        idbRead
          .then((entry) => {
            // If the deferred error was cleared before we got here,
            // don't publish anything.
            if (!deferredError) {
              return;
            }

            const pendingError = deferredError;

            // If the IDB data is fresh enough, publish the error with the cached data.
            if (entry && entry.storedAt + maxFallbackAge > Date.now()) {
              let cachedData: any;

              try {
                cachedData = JSON.parse(entry.data);
              } catch (err) {
                runtime.env.logger?.error('persistence fallback failed', {
                  instanceKey,
                  error: (err as Error).message,
                });
                runtime.next(pendingError.state, pendingError.meta);
                deferredError = null;
                return;
              }

              if (silentFallback) {
                publishSilently(cachedData, pendingError.state.error, {
                  ...pendingError.meta,
                  storedAt: entry.storedAt,
                });
              } else {
                runtime.next(
                  toError(pendingError.state.error)(toSuccess(cachedData)()),
                  pendingError.meta
                );
              }
            } else {
              // If the IDB data is stale, publish the deferred error.
              runtime.next(pendingError.state, pendingError.meta);
            }

            // Clear the deferred error so we don't publish it again.
            deferredError = null;
          })
          .catch(() => {});
      }
    };

    return attach(params, {
      ...runtime,
      next: wrappedNext,
      // Return the deferred error state if it exists, otherwise return the current state.
      // This lets the resolver read its own writes
      getState: () => deferredError?.state ?? runtime.getState(),
    });
  });
